import type { AxiosResponse } from 'axios';
import { match } from 'ts-pattern';
import { z } from 'zod';
import { refreshApi } from '@/features/auth/services/refresh-api';
import {
  getCurrentUser,
  updateUserToken,
} from '@/integrations/idb/models/users';
import type { UsersSchema } from '@/integrations/idb/schemas/users';
import type { MutationResponse, PostRequestBody } from '@/types/api-types';
import type { paths } from '~/generated/auth/api-schema';
import { classifyRefreshFailure } from './refresh-failure';
import { withRefreshLock } from './refresh-lock';

const REFRESH_API_PATH = '/v1/auth/refresh' as const;
export const SESSION_CLEAR_DEADLINE_MS = 3 * 60_000;
type RefreshTokenApiResponse = MutationResponse<paths, typeof REFRESH_API_PATH>;
type RefreshTokenApiPayload = PostRequestBody<paths, typeof REFRESH_API_PATH>;

type RefreshTrigger =
  | { source: 'scheduled'; scheduledFor: Date }
  | { source: 'unauthorized'; attemptedAccessToken: string }
  | { source: 'gate' };

type AuthTokens = Pick<UsersSchema, 'token' | 'refreshToken'>;

type RefreshOutcome =
  | {
      kind: 'authenticated';
      tokens: AuthTokens;
      tokenExpiredAt: Date;
      persistenceFailed: boolean;
    }
  | { kind: 'retryable'; tokenExpiredAt: Date }
  | { kind: 'signed-out' };

type RefreshAttemptResult =
  | { status: 'ok'; tokens: AuthTokens }
  | { status: 'failed'; failure: ReturnType<typeof classifyRefreshFailure> };

type PendingCommit = {
  refreshToken: string;
  tokens: AuthTokens;
};

const tokenSchema = z.object({
  refreshToken: z.string().min(1),
  token: z.string().min(1),
});

const tokensFromUser = (user: UsersSchema): AuthTokens => ({
  token: user.token,
  refreshToken: user.refreshToken,
});

const hasSameSession = (first: UsersSchema, second: UsersSchema) =>
  first.token === second.token &&
  first.refreshToken === second.refreshToken &&
  first.tokenExpiredAt.getTime() === second.tokenExpiredAt.getTime();

export const isPastClearDeadline = (
  user: UsersSchema,
  now = Date.now(),
): boolean => now - user.tokenExpiredAt.getTime() >= SESSION_CLEAR_DEADLINE_MS;

const requestRefresh = async (
  attempted: AuthTokens,
): Promise<RefreshAttemptResult> => {
  try {
    const { data: response } = await refreshApi.post<
      RefreshTokenApiResponse,
      AxiosResponse<RefreshTokenApiResponse>,
      RefreshTokenApiPayload
    >(REFRESH_API_PATH, attempted);

    const parsedTokens = tokenSchema.safeParse(response.data);
    if (!parsedTokens.success) {
      return {
        status: 'failed',
        failure: { reason: 'transient', cause: parsedTokens.error },
      };
    }

    return { status: 'ok', tokens: parsedTokens.data };
  } catch (cause) {
    return { status: 'failed', failure: classifyRefreshFailure(cause) };
  }
};

const transientOutcome = (tokenExpiredAt: Date): RefreshOutcome => ({
  kind: 'retryable',
  tokenExpiredAt,
});

const invalidatedOutcome = (
  reason: 'rejected' | 'deadline',
): RefreshOutcome => {
  console.info('[auth] session invalidated:', reason);
  return { kind: 'signed-out' };
};

let lastConsumed: PendingCommit | null = null;

const commitTokens = async (
  user: UsersSchema,
  tokens: AuthTokens,
): Promise<RefreshOutcome> => {
  try {
    const result = await updateUserToken(user.id, tokens);
    if (result.updatedCount === 0) {
      lastConsumed = null;
      return { kind: 'signed-out' };
    }
    lastConsumed = null;
    return {
      kind: 'authenticated',
      tokens,
      tokenExpiredAt: result.tokenExpiredAt,
      persistenceFailed: false,
    };
  } catch (_cause) {
    return {
      kind: 'authenticated',
      tokens,
      tokenExpiredAt: user.tokenExpiredAt,
      persistenceFailed: true,
    };
  }
};

const adoptUser = (user: UsersSchema): RefreshOutcome => ({
  kind: 'authenticated',
  tokens: tokensFromUser(user),
  tokenExpiredAt: user.tokenExpiredAt,
  persistenceFailed: false,
});

const triggerTokenExpiredAt = (trigger: RefreshTrigger) =>
  trigger.source === 'scheduled' ? trigger.scheduledFor : new Date();

const refreshSession = async (
  trigger: RefreshTrigger,
): Promise<RefreshOutcome> => {
  let current: UsersSchema | undefined;
  try {
    current = await getCurrentUser();
  } catch {
    return transientOutcome(triggerTokenExpiredAt(trigger));
  }

  if (!current) {
    return { kind: 'signed-out' };
  }

  if (
    trigger.source === 'scheduled' &&
    current.tokenExpiredAt.getTime() !== trigger.scheduledFor.getTime()
  ) {
    return adoptUser(current);
  }

  if (
    trigger.source === 'unauthorized' &&
    current.token !== trigger.attemptedAccessToken
  ) {
    return adoptUser(current);
  }

  const attempted = current;

  try {
    return await withRefreshLock(async () => {
      const fresh = await getCurrentUser();
      if (!fresh) {
        return { kind: 'signed-out' };
      }

      if (!hasSameSession(fresh, attempted)) {
        return adoptUser(fresh);
      }

      if (isPastClearDeadline(fresh)) {
        return invalidatedOutcome('deadline');
      }

      if (lastConsumed?.refreshToken === fresh.refreshToken) {
        return commitTokens(fresh, lastConsumed.tokens);
      }

      const attempt = await requestRefresh(tokensFromUser(attempted));
      if (attempt.status === 'ok') {
        lastConsumed = {
          refreshToken: attempted.refreshToken,
          tokens: attempt.tokens,
        };
        return commitTokens(fresh, attempt.tokens);
      }

      const after = await getCurrentUser();
      if (!after) {
        return { kind: 'signed-out' };
      }

      if (!hasSameSession(after, attempted)) {
        return adoptUser(after);
      }

      return match(attempt.failure)
        .with({ reason: 'transient' }, () =>
          transientOutcome(after.tokenExpiredAt),
        )
        .with({ reason: 'rejected' }, async () => {
          if (attempted.refreshToken === lastConsumed?.refreshToken) {
            return {
              kind: 'authenticated' as const,
              tokens: lastConsumed.tokens,
              tokenExpiredAt: attempted.tokenExpiredAt,
              persistenceFailed: true,
            };
          }
          return invalidatedOutcome('rejected');
        })
        .exhaustive();
    });
  } catch {
    return transientOutcome(triggerTokenExpiredAt(trigger));
  }
};

let pendingRefresh: Promise<RefreshOutcome> | null = null;

export const refreshSessionOnce = (
  trigger: RefreshTrigger,
): Promise<RefreshOutcome> => {
  if (pendingRefresh) {
    return pendingRefresh;
  }

  const pending = refreshSession(trigger).finally(() => {
    pendingRefresh = null;
  });
  pendingRefresh = pending;
  return pending;
};
